import type { LegalDocumentSlug, PublicLegalDocument } from "@pufimi/shared";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { ApiError, api } from "@/lib/api";
import { formatDate } from "@/lib/format";
import { Markdown } from "./Markdown";

type LoadState = "loading" | "loaded" | "notPublished" | "error";

// The public legal page (#305): fetches the latest PUBLISHED markdown for the slug
// and renders it in calm long-form typography. A document with no published version
// resolves to a quiet "not yet published" state (not an error) — the empty-CMS
// slice ships before content authoring (#308). The document's own markdown owns the
// page heading; the i18n title labels the transient (not-published / error) states.
// The browser tab title + SEO head are owned by the route's baked `head()` (the
// pure `seo/head` model, ADR-0053 §1) — no runtime `document.title` here.
export function LegalDocumentPage({ slug }: { slug: LegalDocumentSlug }) {
  const { t, i18n } = useTranslation("legal");
  const [state, setState] = useState<LoadState>("loading");
  const [doc, setDoc] = useState<PublicLegalDocument | null>(null);
  const [reloadKey, setReloadKey] = useState(0);

  const title = t(`documents.${slug}`);

  // biome-ignore lint/correctness/useExhaustiveDependencies: reloadKey is the retry TRIGGER — bumping it re-runs the fetch; it is intentionally not read in the body.
  useEffect(() => {
    let active = true;
    setState("loading");
    api.legal.get(slug).then(
      (d) => {
        if (active) {
          setDoc(d);
          setState("loaded");
        }
      },
      (err) => {
        if (!active) return;
        setState(err instanceof ApiError && err.code === "not_found" ? "notPublished" : "error");
      },
    );
    return () => {
      active = false;
    };
  }, [slug, reloadKey]);

  if (state === "loading") {
    return (
      <section className="w-full">
        <Skeleton className="h-9 w-64" />
        <div className="mt-8 space-y-3">
          {["a", "b", "c", "d", "e"].map((k) => (
            <Skeleton key={k} className="h-4 w-full max-w-[68ch]" />
          ))}
        </div>
      </section>
    );
  }

  if (state === "notPublished") {
    return (
      <section className="w-full">
        <h1 className="font-display text-3xl font-semibold tracking-tight [text-wrap:balance]">
          {title}
        </h1>
        <p className="mt-4 max-w-prose text-muted-foreground">{t("state.notPublished")}</p>
      </section>
    );
  }

  if (state === "error" || !doc) {
    return (
      <section className="w-full">
        <h1 className="font-display text-3xl font-semibold tracking-tight [text-wrap:balance]">
          {title}
        </h1>
        <p className="mt-4 max-w-prose text-muted-foreground">{t("state.error")}</p>
        <Button
          type="button"
          variant="outline"
          className="mt-6"
          onClick={() => setReloadKey((k) => k + 1)}
        >
          {t("state.retry")}
        </Button>
      </section>
    );
  }

  const updated = formatDate(doc.publishedAt, i18n.language, { dateStyle: "long" });

  return (
    <section className="w-full">
      <Markdown>{doc.contentMd}</Markdown>
      {updated ? (
        <p className="mt-12 border-t border-border pt-4 text-sm text-muted-foreground">
          {t("lastUpdated", { date: updated })}
        </p>
      ) : null}
    </section>
  );
}
